/**
 * Copyright (c) 2023 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : core.jsx
 * description   : {{insert here}}
 * creation date : 11/11/2023
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    Fragment,
} from 'react';

import {
    I18n,
} from '@aws-amplify/core';

import {
    Flex,
    Heading,
    Text,
    View,
} from '@aws-amplify/ui-react';

import {
    LinkText,
    LinkButton,
} from '@/components/snippets/RouterLink';

import {
    useComponentContext,
} from '@/components/core';

import {
    locale_msg,
} from '@/utils/locale/core';

import {
    auth_routes,
} from '@/utils/router/core';



// --------------------------------------------------------------------------------
//      CONSTANTS DEFINITION
// --------------------------------------------------------------------------------

const auth_components =
{
    Header() {
        return (
            <Fragment>
            </Fragment>
        );
    },

    Footer() {
        return (
            <Fragment>
            </Fragment>
        );
    },

    SignIn: {
        Header() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            level={4}
                            paddingBottom={ctx.theme.tokens.space.medium}
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__SIGN_IN__HEADER)}
                        </Heading>
                    </View>
                </Fragment>
            );
        },

        Footer() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        paddingTop={ctx.theme.tokens.space.large}
                        fontSize={ctx.theme.tokens.fontSizes.small}
                    >
                        <Flex
                            direction='row'
                            justifyContent='flex-start'
                            alignItems='center'
                            alignContent='center'
                            wrap='wrap'
                            gap={ctx.theme.tokens.space.zero}
                            paddingBottom={ctx.theme.tokens.space.xxs}
                        >
                            <LinkText
                                to={
                                    ctx.api.utils.buildUrlAuth(
                                        auth_routes.RESET_PASSWORD
                                    )
                                }
                                variation='tertiary'
                            >
                                {/* TODO: Translate message */}
                                Forgot password?
                            </LinkText>
                        </Flex>

                        <Flex
                            direction='row'
                            justifyContent='flex-start'
                            alignItems='center'
                            alignContent='center'
                            wrap='wrap'
                            gap={ctx.theme.tokens.space.zero}
                        >
                            <Text
                                variation='tertiary'
                            >
                                {/* TODO: Translate message */}
                                Don't have an account?&nbsp;
                            </Text>

                            <LinkText
                                to={
                                    ctx.api.utils.buildUrlAuth(
                                        auth_routes.SIGN_UP
                                    )
                                }
                                variation='tertiary'
                            >
                                {/* TODO: Translate message */}
                                Sign up
                            </LinkText>
                        </Flex>
                    </View>
                </Fragment>
            );
        },
    },

    SignUp: {
        Header() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            level={4}
                            paddingBottom={ctx.theme.tokens.space.medium}
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__CREATE_ACCOUNT__HEADER)}
                        </Heading>
                    </View>
                </Fragment>
            );
        },

        Footer() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                        paddingTop={ctx.theme.tokens.space.large}
                    >
                        <LinkButton
                            to={
                                ctx.api.utils.buildUrlAuth(
                                    auth_routes.SIGN_IN
                                )
                            }
                            fontWeight='normal'
                            size='small'
                            variation='link'
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__CREATE_ACCOUNT__BACK_TO_SIGN_IN)}
                        </LinkButton>
                    </View>
                </Fragment>
            );
        },
    },

    ConfirmSignUp: {
        Header() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            level={4}
                            paddingBottom={ctx.theme.tokens.space.medium}
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__CONFIRM_CREATE_ACCOUNT__HEADER)}
                        </Heading>
                    </View>
                </Fragment>
            );
        },
    },

    ResetPassword: {
        Header() {
            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            level={4}
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__RESET_PASSWORD__HEADER)}
                        </Heading>
                    </View>
                </Fragment>
            );
        },

        Footer() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <LinkButton
                            to={
                                ctx.api.utils.buildUrlAuth(
                                    auth_routes.SIGN_IN
                                )
                            }
                            fontWeight='normal'
                            size='small'
                            variation='link'
                            marginTop={ctx.theme.tokens.space.small}
                            className='custom-button-link'
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__CREATE_ACCOUNT__BACK_TO_SIGN_IN)}
                        </LinkButton>
                    </View>
                </Fragment>
            );
        },
    },

    ConfirmResetPassword: {
        Header() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            paddingTop={ctx.theme.tokens.space.small}
                            level={4}
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__CONFIRM_RESET_PASSWORD__HEADER)}
                        </Heading>
                    </View>
                </Fragment>
            );
        },
    },

    ForceNewPassword: {
        Header() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            level={4}
                            paddingBottom={ctx.theme.tokens.space.medium}
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__FORCE_NEW_PASSWORD__HEADER)}
                        </Heading>
                    </View>
                </Fragment>
            );
        },
    },

    VerifyUser: {
        Header() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            level={4}
                            paddingBottom={ctx.theme.tokens.space.medium}
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__VALIDATE_PASSWORD__HEADER)}
                        </Heading>
                    </View>
                </Fragment>
            );
        },
    },

    ConfirmVerifyUser: {
        Header() {
            // Component context
            const { ctx } = useComponentContext();

            return (
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            level={4}
                            paddingBottom={ctx.theme.tokens.space.medium}
                        >
                            {I18n.get(locale_msg.COGNITO_AUTH__CONFIRM_VALIDATE_PASSWORD__HEADER)}
                        </Heading>
                    </View>
                </Fragment>
            );
        },
    },
};

const auth_form_fields =
{
    signIn: {
        username: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__EMAIL),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__EMAIL),
        },
        password: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__PASSWORD),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__PASSWORD),
        },
    },

    signUp: {
        email: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__EMAIL),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__EMAIL),
            order: 1,
        },
        name: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__NAME),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__NAME),
            order: 2,
        },
        family_name: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__FAMILY_NAME),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__FAMILY_NAME),
            order: 3,
        },
        nickname: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__NICKNAME),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__NICKNAME),
            order: 4,
        },
        phone_number: {
            dialCode: '+33',
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__PHONE_NUMBER),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__PHONE_NUMBER),
            order: 5,
        },
        password: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__PASSWORD),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__PASSWORD),
            isRequired: false,
            order: 6,
        },
        confirm_password: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__CONFIRM_PASSWORD),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__CONFIRM_PASSWORD),
            order: 7,
        },
    },

    confirmSignUp: {
        confirmation_code: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__CONFIRMATION_CODE),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__CONFIRMATION_CODE),
            isRequired: false,
        },
    },

    resetPassword: {
        username: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__EMAIL),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__EMAIL),
        },
    },

    confirmResetPassword: {
        confirmation_code: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__CONFIRMATION_CODE),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__CONFIRMATION_CODE),
            isRequired: false,
        },
        password: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__NEW_PASSWORD),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__NEW_PASSWORD),
            isRequired: false,
        },
        confirm_password: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__CONFIRM_NEW_PASSWORD),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__CONFIRM_NEW_PASSWORD),
        },
    },

    forceNewPassword: {
        password: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__PASSWORD),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__PASSWORD),
        },
        confirm_password: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__CONFIRM_PASSWORD),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__CONFIRM_PASSWORD),
        },
    },

    verifyUser: {},

    confirmVerifyUser: {
        confirmation_code: {
            label: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__LABEL__CONFIRMATION_CODE),
            placeholder: I18n.get(locale_msg.COGNITO_AUTH__AUTH_FORM_TEXT__PLACEHOLDER__CONFIRMATION_CODE),
            isRequired: false,
        },
    },
};

const auth_props =
{
    components: auth_components,
    formFields: auth_form_fields,
    variation: 'default',
};



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export {
    auth_props,
};



// End of file
